import { useState, useEffect, useCallback } from "react";
import {
  ChevronRight,
  ArrowLeft,
  CheckCircle,
  MapPin,
  Edit,
  Lock,
  Truck,
  Plus,
  X,
  Star,
  Phone,
  Edit2,
  Gift,
  Mail,
  MessageSquare,
  CreditCard,
  Banknote,
} from "lucide-react";
import BaseApi from "../services/base-api";
import { Cart } from "../types/Cart";
import { useToast } from "../context/ToastContext";
import { useNavigate, useLocation } from "react-router-dom";
import ContactInfo from "../components/ContactInfo";
import { useTranslation } from "react-i18next";
import Price from "../components/Currency/Price";
import MyFatoorahPayment from "./MyFatoorahPayment";
import { WhatsAppService } from "../services/whatsAppService";
import LoadingComponent from "../components/LoadingComponent";
import { LocalizedText } from "../types/LocalizedText";
import {
  AddressFormData,
  CITIES_BY_COUNTRY,
  COUNTRIES,
  Country,
} from "./Address";
import { COUNTRY_CODES } from "../constants/countryCodes";

// Types
interface Address {
  id: number;
  street: string;
  apartment: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  fullName: string;
  phone: string;
  isDefault: boolean;
}

interface Payments {
  id: string;
  name: LocalizedText;
  type: string;
  description?: LocalizedText;
  icon?: any;
}

interface GiftInfo {
  isGift: boolean;
  recipientInfo: {
    recipientName: string;
    recipientEmail: string;
    recipientPhone: string;
  };
  giftMessage: string;
}

interface CheckoutSuccessData {
  orderId: string;
  totalAmount: number;
  paymentMethodName: LocalizedText;
  deliveryAddress: string;
}

type ValidationErrors = Partial<Record<keyof AddressFormData, string>>;
const CHECKOUT_SUCCESS_STORAGE_KEY = "checkout_success_data";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentMethods, setPaymentMethods] = useState<Payments[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<
    Payments | undefined
  >();
  const [paymentProvider, setPaymentProvider] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [orderSuccessData, setOrderSuccessData] =
    useState<CheckoutSuccessData | null>(null);
  const [destination, setDestination] = useState("");

  // Gift-related state from CartPage
  const [giftInfo, setGiftInfo] = useState<GiftInfo>({
    isGift: false,
    recipientInfo: {
      recipientName: "",
      recipientEmail: "",
      recipientPhone: "",
    },
    giftMessage: "",
  });

  // Address related states
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [cart, setCart] = useState<Cart | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingRateResolved, setShippingRateResolved] = useState(false);
  const [shippingEstimatedDays, setShippingEstimatedDays] = useState("");

  // Track manual input mode for each field
  // const [isManualZip, setIsManualZip] = useState<boolean>(false);
  const [isManualCity, setIsManualCity] = useState<boolean>(false);
  // const [isManualState, setIsManualState] = useState<boolean>(false);

  const [formData, setFormData] = useState<AddressFormData>({
    street: "",
    city: "",
    country: "QA",
    fullName: "",
    phone: "",
    isDefault: false,
    // Qatar-specific fields
    zone: "",
    buildingNumber: "",
    unitNumber: "",
  });

  const availableCities = CITIES_BY_COUNTRY[formData.country] || [];
  const [errors, setErrors] = useState<ValidationErrors>({});

  const { success, err } = useToast();
  const { t, i18n } = useTranslation();

  const saveCheckoutSuccessData = (data: CheckoutSuccessData) => {
    sessionStorage.setItem(CHECKOUT_SUCCESS_STORAGE_KEY, JSON.stringify(data));
  };

  const getCheckoutSuccessData = (): CheckoutSuccessData | null => {
    try {
      const raw = sessionStorage.getItem(CHECKOUT_SUCCESS_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CheckoutSuccessData) : null;
    } catch (error) {
      console.error("Failed to parse checkout success data:", error);
      return null;
    }
  };

  const clearCheckoutSuccessData = () => {
    sessionStorage.removeItem(CHECKOUT_SUCCESS_STORAGE_KEY);
  };

  const notifyCartCleared = () => {
    window.dispatchEvent(
      new CustomEvent("cartUpdated", {
        detail: { action: "clear", count: 0 },
      }),
    );
  };

  const calculateTotalWeight = useCallback((cartData: any): number => {
    return cartData.items.reduce((total: number, item: any) => {
      const weightPerItem = item?.variation?.weight || 0;
      return total + weightPerItem * item.quantity;
    }, 0);
  }, []);

  const getDestinationFromCountry = (country?: string) => {
    if (!country) return "";
    const matchedCountry = COUNTRY_CODES.find((c) => c.country === country);
    return matchedCountry?.name || country;
  };

  const fetchShippingRates = useCallback(
    async (cartData: any, destinationName: string) => {
      try {
        const weight = calculateTotalWeight(cartData);
        if (!weight) {
          setShippingEstimatedDays("");
          setShippingRateResolved(true);
          return;
        }

        setShippingLoading(true);

        const res = await BaseApi.post("/shipping/rates", {
          weight,
          destination: destinationName,
        });

        console.log("Shipping rate response:", res);

        if (res.success && res.data) {
          setShippingEstimatedDays(res.data.estimatedDays || "");
          setCart({
            ...cartData,
            shipping: Number(res.data.rate),
          });
        } else {
          setShippingEstimatedDays("");
        }
      } catch (error) {
        setShippingEstimatedDays("");
        console.error("Failed to fetch shipping rates:", error);
      } finally {
        setShippingLoading(false);
        setShippingRateResolved(true);
      }
    },
    [calculateTotalWeight],
  );

  // Extract gift info from navigation state
  useEffect(() => {
    if (location.state) {
      const { isGift, recipientInfo, giftMessage } = location.state as any;
      if (isGift) {
        setGiftInfo({
          isGift: isGift || false,
          recipientInfo: recipientInfo || { name: "", email: "", phone: "" },
          giftMessage: giftMessage || "",
        });
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.state) return;

    const state = location.state as {
      paymentSuccess?: boolean;
      paymentFailed?: boolean;
      orderId?: string;
      message?: string;
    };

    if (state.paymentSuccess) {
      const storedData = getCheckoutSuccessData();
      const resolvedOrderId = state.orderId || storedData?.orderId || "";

      if (
        storedData &&
        (!state.orderId || storedData.orderId === state.orderId)
      ) {
        setOrderSuccessData(storedData);
      } else if (resolvedOrderId) {
        setOrderSuccessData({
          orderId: resolvedOrderId,
          totalAmount: Number(cart?.total || 0),
          paymentMethodName: {
            en: selectedPayment?.name?.en || "Online",
            ar: selectedPayment?.name?.ar || "عبر الإنترنت",
          },
          deliveryAddress: selectedAddress?.street || "",
        });
      }

      if (resolvedOrderId) {
        setOrderId(resolvedOrderId);
      }
      setShowPaymentModal(false);
      setOrderPlaced(true);
      notifyCartCleared();
      success(t("Payment successful! Your order is confirmed"));
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    if (state.paymentFailed) {
      setShowPaymentModal(false);
      err(state.message || t("Payment failed or was cancelled"));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [
    location.state,
    location.pathname,
    navigate,
    success,
    err,
    t,
    cart?.total,
    selectedPayment?.name?.en,
    selectedPayment?.name?.ar,
    selectedAddress?.street,
  ]);

  const shippingWeight = cart ? calculateTotalWeight(cart) : 0;

  useEffect(() => {
    if (destination) {
      setShippingRateResolved(false);
    }
  }, [destination, shippingWeight]);

  useEffect(() => {
    // console.log("Fetching shipping rates for destination:", destination);
    if (cart && destination && !shippingLoading && !shippingRateResolved) {
      fetchShippingRates(cart, destination);
    }
  }, [
    cart,
    destination,
    shippingLoading,
    shippingRateResolved,
    fetchShippingRates,
  ]);

  // Fetch addresses on mount
  useEffect(() => {
    fetchAddresses();
    fetchPaymentMethods();
  }, []);

  const fetchAddresses = async () => {
    setIsLoadingAddresses(true);
    try {
      const res = await BaseApi.get(
        `/addresses?page=${currentPage}&limit=${itemsPerPage}`,
      );
      if (res?.data) {
        setAddresses(res.data);
        const defaultAddr = res.data.find((addr: Address) => addr.isDefault);
        if (defaultAddr) {
          const destinationName = getDestinationFromCountry(
            defaultAddr.country,
          );
          setDestination(destinationName);
          setSelectedAddress(defaultAddr);
        } else if (res.data.length > 0) {
          setSelectedAddress(res.data[0]);
          setDestination(getDestinationFromCountry(res.data[0].country));
        }
      }
    } catch (error) {
      console.log("Failed to fetch addresses", error);
    } finally {
      setIsLoadingAddresses(false);
      fetchCartItems();
    }
  };

  const fetchPaymentMethods = async () => {
    setIsLoadingPaymentMethods(true);
    try {
      const methods: Payments[] = [
        {
          id: "online",
          name: { en: "Online", ar: "عبر الإنترنت" },
          type: "online",
          icon: <CreditCard size={24} />,
        },
      ];

      const res = await BaseApi.get("/settings/public");
      const isPayAndDeliverEnabled = !!res?.data?.pay_and_deliver;
      setPaymentProvider(
        res?.data?.payment_provider || res?.data?.paymentProvider,
      );

      if (isPayAndDeliverEnabled) {
        methods.push({
          id: "cash",
          name: { en: "Pay and Deliver", ar: "الدفع عند الاستلام" },
          type: "cash",
          icon: <Banknote size={24} />,
        });
      }

      setPaymentMethods(methods);
    } catch (error) {
      console.error("Failed to fetch payment methods settings:", error);
      setPaymentMethods([
        {
          id: "online",
          name: { en: "Online", ar: "عبر الإنترنت" },
          type: "online",
          icon: <CreditCard size={24} />,
        },
      ]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  // const fetchPaymentStatus = async (paymentId: number, keyType: string) => {
  //   try {
  //     const res = await BaseApi.post(`/payments/callback`, {
  //       paymentId,
  //       keyType,
  //     });
  //     if (res?.data) {
  //       console.log("res - ", res);
  //     }
  //   } catch (error) {
  //     console.log("Failed to fetch payment methods", error);
  //   } finally {
  //   }
  // };

  const fetchCartItems = async () => {
    try {
      const res = await BaseApi.get("/cart");
      if (res.success) {
        setCart(res.data);
      }
    } catch (error) {
      console.error("Failed to load cart items:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      street: "",
      // apartment: "",
      city: "",
      // state: "",
      country: "QA",
      // zipCode: "",
      fullName: "",
      phone: "",
      isDefault: false,
      // Qatar-specific fields
      zone: "",
      buildingNumber: "",
      unitNumber: "",
    });
    setErrors({});
  };

  const openAddressModal = () => {
    setShowAddressModal(true);
  };

  const closeAddressModal = () => {
    setShowAddressModal(false);
  };

  const openAddModal = () => {
    resetForm();
    setEditingAddress(null);
    setShowAddressModal(false);
    setShowAddModal(true);
  };

  const openEditModal = (address: Address) => {
    setFormData(address);
    setEditingAddress(address.id);
    setShowAddressModal(false);
    setShowAddModal(true);
  };

  const closeAddModalHandler = () => {
    setShowAddModal(false);
    setEditingAddress(null);
    resetForm();
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    const isQatar = formData.country === "QA";

    // Apartment validation (not required for Qatar if zone and building number are provided)
    // if (!isQatar && !formData.apartment.trim()) {
    //   newErrors.apartment = t("Apartment is required");
    // } else if (formData.apartment.trim() && formData.apartment.trim().length > 50) {
    //   newErrors.apartment = t("Apartment must not exceed 50 characters");
    // }

    // Qatar-specific validations
    if (isQatar) {
      // Zone validation (mandatory for Qatar)
      if (!formData.zone?.trim()) {
        newErrors.zone = t("Zone is required");
      } else if (formData.zone.trim().length < 1) {
        newErrors.zone = t("Zone must be at least 1 character");
      } else if (formData.zone.trim().length > 10) {
        newErrors.zone = t("Zone must not exceed 10 characters");
      } else if (!/^[0-9]+$/.test(formData.zone.trim())) {
        newErrors.zone = t("Zone must contain only numbers");
      }

      // Building Number validation (mandatory for Qatar)
      if (!formData.buildingNumber?.trim()) {
        newErrors.buildingNumber = t("Building Number is required");
      } else if (formData.buildingNumber.trim().length < 1) {
        newErrors.buildingNumber = t(
          "Building Number must be at least 1 character",
        );
      } else if (formData.buildingNumber.trim().length > 10) {
        newErrors.buildingNumber = t(
          "Building Number must not exceed 10 characters",
        );
      } else if (!/^[0-9]+$/.test(formData.buildingNumber.trim())) {
        newErrors.buildingNumber = t(
          "Building Number must contain only numbers",
        );
      }

      // Unit Number validation (optional for Qatar)
      // Unit Number validation (optional for Qatar)
      // if (!formData.unitNumber?.trim()) {
      //   newErrors.unitNumber = t("Unit Number is required");
      // } else if (formData.unitNumber.trim().length < 1) {
      //   newErrors.unitNumber = t("Unit Number must be at least 1 character");
      // } else
      if (formData.unitNumber && formData.unitNumber?.trim().length > 10) {
        newErrors.unitNumber = t("Unit Number must not exceed 10 characters");
      } else if (
        formData.unitNumber &&
        !/^[0-9]+$/.test(formData.unitNumber.trim())
      ) {
        newErrors.unitNumber = t("Unit Number must contain only numbers");
      }
      // if (formData.unitNumber?.trim()) {
      //   if (formData.unitNumber.trim().length > 10) {
      //     newErrors.unitNumber = t("Unit Number must not exceed 10 characters");
      //   } else if (!/^[a-zA-Z0-9\-]+$/.test(formData.unitNumber.trim())) {
      //     newErrors.unitNumber = t("Unit Number can only contain letters, numbers and hyphens");
      //   }
      // }
    } else {
      // console.log("else -----> ");
      // Street validation
      if (!formData.street.trim()) {
        newErrors.street = t("Street is required");
      } else if (formData.street.trim().length < 3) {
        newErrors.street = t("Street must be at least 3 characters");
      } else if (formData.street.trim().length > 50) {
        newErrors.street = t("Street must not exceed 50 characters");
      }
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = t("City is required");
    } else if (formData.city.trim().length < 2) {
      newErrors.city = t("City must be at least 2 characters");
    } else if (formData.city.trim().length > 50) {
      newErrors.city = t("City must not exceed 50 characters");
    } else if (!/^[a-zA-Z\s\-.']+$/.test(formData.city.trim())) {
      newErrors.city = t(
        "City can only contain letters, spaces, hyphens, dots and apostrophes",
      );
    }

    // State validation
    // if (!formData.state.trim()) {
    //   newErrors.state = t("State is required");
    // } else if (formData.state.trim().length < 2) {
    //   newErrors.state = t("State must be at least 2 characters");
    // } else if (formData.state.trim().length > 50) {
    //   newErrors.state = t("State must not exceed 50 characters");
    // } else if (!/^[a-zA-Z\s\-.']+$/.test(formData.state.trim())) {
    //   newErrors.state = t("State can only contain letters, spaces, hyphens, dots and apostrophes");
    // }

    // Country validation
    if (!formData.country.trim()) {
      newErrors.country = t("Country is required");
    }

    // Zip Code validation
    // if (!formData.zipCode.trim()) {
    //   newErrors.zipCode = t("Zip Code is required");
    // } else if (formData.zipCode.trim().length < 3) {
    //   newErrors.zipCode = t("Zip Code must be at least 3 characters");
    // } else if (formData.zipCode.trim().length > 10) {
    //   newErrors.zipCode = t("Zip Code must not exceed 10 characters");
    // } else if (!/^[a-zA-Z0-9\s\-]+$/.test(formData.zipCode.trim())) {
    //   newErrors.zipCode = t("Zip Code can only contain letters, numbers, spaces and hyphens");
    // }

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = t("Full name is required");
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = t("Full name must be at least 2 characters");
    } else if (formData.fullName.trim().length > 100) {
      newErrors.fullName = t("Full name must not exceed 100 characters");
    } else if (!/^[a-zA-Z\s\-.']+$/.test(formData.fullName.trim())) {
      newErrors.fullName = t(
        "Full name can only contain letters, spaces, hyphens, dots and apostrophes",
      );
    } else if (formData.fullName.trim().split(/\s+/).length < 2) {
      newErrors.fullName = t("Please enter both first and last name");
    }

    // if (
    //   formData.isDefault &&
    //   addresses.some((a) => a.isDefault && a.id !== editingAddress)
    // ) {
    //   err("Please make sure you have one default address!");
    // }

    // Phone validation (optional but if provided must be valid)
    if (formData.phone.trim()) {
      // Remove spaces, hyphens, and parentheses for validation
      const cleanPhone = formData.phone.replace(/[\s\-()]/g, "");

      if (!/^\+?[0-9]+$/.test(cleanPhone)) {
        newErrors.phone = t(
          "Phone number can only contain numbers, +, spaces, hyphens and parentheses",
        );
      } else if (cleanPhone.length < 8) {
        newErrors.phone = t("Phone number must be at least 8 digits");
      } else if (cleanPhone.length > 15) {
        newErrors.phone = t("Phone number must not exceed 15 digits");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof AddressFormData,
    value: string | boolean,
  ) => {
    setFormData({ ...formData, [field]: value });
    if (field in errors) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleSave = async () => {
    if (validateForm()) {
      setIsSaving(true);
      try {
        if (editingAddress) {
          const res = await BaseApi.put(
            `/addresses/${editingAddress}`,
            formData,
          );
          if (res?.data) {
            await fetchAddresses();
          }
        } else {
          const res = await BaseApi.post("/addresses", formData);
          if (res?.data) {
            await fetchAddresses();
            success(t("Address has been saved for later"));
          }
        }
        closeAddModalHandler();
      } catch (error) {
        console.error("Failed to save address!");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSelectAddress = (address: Address) => {
    setSelectedAddress(address);
    setDestination(getDestinationFromCountry(address.country));
    closeAddressModal();
  };

  const handlePaymentSelect = (payment: Payments) => {
    setSelectedPayment(payment);
  };

  const handleContinueToReview = () => {
    setCurrentStep(2);
    if (!selectedAddress && !giftInfo.isGift) {
      setShowAddModal(true);
      return;
    }
    if (selectedPayment) {
      setCurrentStep(2);
    }
  };

  const handlePlaceOrder = async () => {
    setIsProcessing(true);

    try {
      const res = await BaseApi.post("/orders/from-cart", {
        ...giftInfo,
        shippingAddressId: selectedAddress?.id,
        paymentMethod: selectedPayment?.id,
      });

      if (res?.data) {
        const newOrderId = res.data.id;
        const checkoutData: CheckoutSuccessData = {
          orderId: newOrderId,
          totalAmount: Number(res.data?.total ?? cart?.total ?? 0),
          paymentMethodName: {
            en: selectedPayment?.name?.en || "Online",
            ar: selectedPayment?.name?.ar || "عبر الإنترنت",
          },
          deliveryAddress: selectedAddress?.street || "",
        };

        setOrderId(newOrderId);
        setOrderSuccessData(checkoutData);
        saveCheckoutSuccessData(checkoutData);

        if (selectedPayment?.id !== "cash") {
          setShowPaymentModal(true);
        } else {
          setOrderPlaced(true);
          notifyCartCleared();
          success("Your order is placed");
        }
      } else {
        err("Failed to place your order, Please try again later");
      }
    } catch (error) {
      console.error("Order placement error:", error);
      console.error("Failed to place your order, Please try again later");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setOrderPlaced(true);
    notifyCartCleared();
    const storedData = getCheckoutSuccessData();
    if (storedData) {
      setOrderSuccessData(storedData);
      setOrderId(storedData.orderId);
    }
    success("Payment successful! Your order is confirmed");
    try {
      // Send order confirmation via WhatsApp
      await WhatsAppService.sendOrderConfirmation(orderId);
    } catch (error) {
      console.error("Failed to send WhatsApp notification:", error);
      // Still proceed to success page
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    navigate("/account/orders");
    window.location.reload();
    err("Payment cancelled. Your order is saved but not confirmed.");
  };

  const handlePaymentError = (error: string) => {
    err(error);
  };

  const selectedPaymentMethod = paymentMethods.find(
    (p) => p.id === selectedPayment?.id,
  );
  const isPayAndDeliver =
    selectedPaymentMethod?.id === "cash" ||
    selectedPayment?.id === "cash" ||
    orderSuccessData?.paymentMethodName?.en
      ?.toLowerCase()
      .includes("pay and deliver");

  const renderTextField = (
    key:
      | "street"
      | "fullName"
      | "phone"
      | "zone"
      | "buildingNumber"
      | "unitNumber",
    label: string,
    required: boolean = true,
  ) => {
    const maxLengths: Record<typeof key, number> = {
      street: 50,
      // apartment: 50,
      fullName: 100,
      phone: 15,
      zone: 10,
      buildingNumber: 10,
      unitNumber: 10,
    };

    const currentLength = formData[key]?.length || 0;
    const maxLength = maxLengths[key];
    const isNearLimit = currentLength > maxLength * 0.8;

    // Get placeholder based on field
    const getPlaceholder = () => {
      if (key === "zone") return t("Enter Zone Number");
      if (key === "buildingNumber") return t("Enter Building number");
      if (key === "unitNumber") return t("Enter Unit Number (optional)");
      if (key === "street") return t("Enter Street Address");
      return t(`Enter ${label}`);
    };

    // Get helper text
    const getHelperText = () => {
      if (key === "zone" || key === "buildingNumber") return t("Numbers only");
      if (key === "unitNumber") return t("Numbers only (optional)");
      if (key === "phone" && formData[key])
        return t("Format: +974 1234 5678 or 12345678");
      return null;
    };

    return (
      <div>
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
          {required && <span className="text-red-500">*</span>} {label}
          {currentLength > 0 && (
            <span
              className={`float-right text-xs ${isNearLimit ? "text-orange-600" : "text-gray-500"}`}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </label>
        <input
          type="text"
          value={formData[key] || ""}
          onChange={(e) => handleInputChange(key, e.target.value)}
          maxLength={maxLength}
          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
            errors[key] ? "border-red-500" : "border-gray-200"
          } focus:border-black focus:outline-none transition-all duration-300`}
          placeholder={getPlaceholder()}
        />
        {errors[key] && (
          <p className="mt-1 text-xs sm:text-sm text-red-500">{errors[key]}</p>
        )}
        {!errors[key] && getHelperText() && (
          <p className="mt-1 text-xs text-gray-500">{getHelperText()}</p>
        )}
        {!errors[key] &&
          key === "fullName" &&
          formData[key] &&
          formData[key]!.trim().split(/\s+/).length === 1 && (
            <p className="mt-1 text-xs text-orange-600">
              {t("Please include both first and last name")}
            </p>
          )}
      </div>
    );
  };

  const renderSelectField = (
    key: "country" | "city",
    label: string,
    options: string[] | Country[],
    required: boolean = true,
  ) => {
    const isCountryField = key === "country";
    const isManualMode =
      // (key === "state" && isManualState) ||
      key === "city" && isManualCity;
    // ||
    // (key === "zipCode" && isManualZip);

    const maxLengths = {
      country: 2,
      state: 50,
      city: 50,
      zipCode: 10,
    };

    const currentLength = formData[key].length;
    const maxLength = maxLengths[key];
    const isNearLimit = currentLength > maxLength * 0.8;

    // Handler for when "other" is selected
    const handleSelectChange = (value: string) => {
      if (value === "other") {
        // if (key === "state") {
        //   setIsManualState(true);
        //   handleInputChange(key, "");
        // } else
        if (key === "city") {
          setIsManualCity(true);
          handleInputChange(key, "");
        }
        // else if (key === "zipCode") {
        //   setIsManualZip(true);
        //   handleInputChange(key, "");
        // }
      } else {
        handleInputChange(key, value);
      }
    };

    // Handler to switch back to dropdown
    const switchToDropdown = () => {
      // if (key === "state") {
      //   setIsManualState(false);
      //   handleInputChange(key, "");
      // } else
      if (key === "city") {
        setIsManualCity(false);
        handleInputChange(key, "");
      }
      // else if (key === "zipCode") {
      //   setIsManualZip(false);
      //   handleInputChange(key, "");
      // }
    };

    return (
      <div>
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
          {required && <span className="text-red-500">*</span>} {label}
          {isManualMode && currentLength > 0 && (
            <span
              className={`float-right text-xs ${isNearLimit ? "text-orange-600" : "text-gray-500"}`}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </label>

        {isManualMode ? (
          <div className="relative">
            <input
              type="text"
              value={formData[key]}
              onChange={(e) => handleInputChange(key, e.target.value)}
              maxLength={maxLength}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
                errors[key] ? "border-red-500" : "border-gray-200"
              } focus:border-black focus:outline-none transition-all duration-300 pr-20`}
              placeholder={t(`Enter ${label}`)}
            />
            <button
              type="button"
              onClick={switchToDropdown}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-semibold bg-[#0d223f] hover:bg-[#0d223f] px-2 sm:px-3 py-1 rounded transition-colors"
            >
              {t("Select")}
            </button>
          </div>
        ) : (
          <select
            value={formData[key]}
            onChange={(e) => handleSelectChange(e.target.value)}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
              errors[key] ? "border-red-500" : "border-gray-200"
            } focus:border-black focus:outline-none transition-all duration-300 bg-white`}
          >
            <option value="">{t(`Select ${label}`)}</option>
            {isCountryField
              ? (options as Country[]).map((country) => (
                  <option key={country.country} value={country.country}>
                    {country.flag} {country.name}
                  </option>
                ))
              : (options as string[]).map((option) => (
                  <option key={option} value={option}>
                    {option === "other" ? t("Other (Enter manually)") : option}
                  </option>
                ))}
          </select>
        )}

        {errors[key] && (
          <p className="mt-1 text-xs sm:text-sm text-red-500">{errors[key]}</p>
        )}

        {!isCountryField && !isManualMode && !errors[key] && !formData[key] && (
          <p className="mt-1 text-xs text-gray-500">
            {t("Can't find your")} {label.toLowerCase()}?{" "}
            {t("Select 'Other' to enter manually")}
          </p>
        )}

        {/* {isManualMode && !errors[key] && (
          <p className="mt-1 text-xs text-blue-600">
            {key === "zipCode" 
              ? t("Enter alphanumeric characters only (e.g., 12345 or AB123)")
              : t("Enter letters only (no numbers or special characters)")
            }
          </p>
        )} */}
      </div>
    );
  };

  // Order Success Screen
  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center animate-scale-in">
            <div className="relative mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full mx-auto flex items-center justify-center animate-bounce-in">
                <CheckCircle
                  size={48}
                  className="text-green-600 animate-check"
                />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 border-4 border-green-200 rounded-full animate-ping-slow"></div>
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t("Order Placed Successfully!")}
            </h1>
            <p className="text-gray-600 mb-6">
              {t("Thank you for your purchase")}
            </p>

            {isPayAndDeliver && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <div className="bg-green-600 rounded-full p-2">
                    <MessageSquare size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-700">
                      {t("Payment Link Sent")}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      {t(
                        "We have sent a payment link to your WhatsApp. Please complete payment to confirm delivery.",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-6 mb-6 text-left">
              <div className="flex flex-col items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">{t("Order ID")}</span>
                <span className="text-sm font-bold text-gray-900">
                  {orderSuccessData?.orderId || orderId}
                </span>
              </div>

              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {t("Total Amount")}
                </span>
                <span className="text-lg font-bold text-green-600">
                  <Price
                    amount={Number(
                      orderSuccessData?.totalAmount ?? cart?.total ?? 0,
                    )}
                  />
                </span>
              </div>

              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {t("Payment Method")}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {i18n.language === "ar"
                    ? orderSuccessData?.paymentMethodName?.ar ||
                      selectedPaymentMethod?.name?.ar
                    : orderSuccessData?.paymentMethodName?.en ||
                      selectedPaymentMethod?.name?.en}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {t("Delivery Address")}
                </span>
                <span className="text-sm font-semibold text-gray-900 text-right">
                  {orderSuccessData?.deliveryAddress ||
                    selectedAddress?.street ||
                    "-"}
                </span>
              </div>
            </div>

            <div className="bg-gray-400 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 text-left">
                <div className="bg-black rounded-full p-2">
                  <Truck size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {t("Estimated Delivery")}
                  </p>
                  <p className="text-xs text-white">
                    {new Date(
                      Date.now() + 3 * 24 * 60 * 60 * 1000,
                    ).toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  clearCheckoutSuccessData();
                  navigate("/account/orders");
                }}
                className="w-full bg-black hover:bg-black-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {t("Track Order")}
              </button>
              <button
                onClick={() => {
                  clearCheckoutSuccessData();
                  navigate("/home");
                }}
                className="w-full bg-gray-100 hover:bg-gray-400 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {t("Continue Shopping")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2e6db]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() =>
            currentStep === 1 ? window.history.back() : setCurrentStep(1)
          }
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">{t("Back")}</span>
        </button>

        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold mb-2 transition-all ${
                  currentStep >= 1
                    ? "bg-black text-white"
                    : "bg-gray-400 text-gray-500"
                }`}
              >
                {currentStep > 1 ? <CheckCircle size={24} /> : "1"}
              </div>
              <span className="text-xs font-medium text-gray-600">
                {t("Payment mode")}
              </span>
            </div>

            <div
              className={`flex-1 h-1 mx-2 transition-all ${
                currentStep >= 2 ? "bg-black" : "bg-gray-400"
              }`}
            />

            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold mb-2 transition-all ${
                  currentStep >= 2
                    ? "bg-black text-white"
                    : "bg-gray-400 text-white"
                }`}
              >
                2
              </div>
              <span className="text-xs font-medium text-gray-600">
                {t("Review your order")}
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: Payment Method Selection */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t("Select Payment Method")}
              </h1>
              <p className="text-gray-600">
                {t("Choose how you'd like to pay")}
              </p>
            </div>

            <div className="space-y-4">
              {isLoadingPaymentMethods ? (
                <div className="min-h-[200px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                      <div className="absolute inset-0 border-4 border-gray-300 rounded-full animate-ping"></div>
                      <div className="absolute inset-0 border-4 border-t-gray-600 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-600">
                      {t("Loading Payment Methods...")}
                    </p>
                  </div>
                </div>
              ) : (
                paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => handlePaymentSelect(method)}
                    className={`w-full bg-white rounded-xl p-6 border-2 transition-all ${
                      selectedPayment?.id === method.id
                        ? "border-black shadow-lg scale-[1.02]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {method?.icon}

                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {i18n.language === "ar"
                            ? method.name.ar
                            : method.name.en}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {i18n.language === "ar"
                            ? method?.description?.ar
                            : method?.description?.en}
                        </p>
                      </div>

                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedPayment?.id === method.id
                            ? "border-black bg-black"
                            : "border-gray-300"
                        }`}
                      >
                        {selectedPayment?.id === method.id && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <button
              onClick={handleContinueToReview}
              disabled={!selectedPayment}
              className="w-full bg-black hover:bg-black-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              {!giftInfo.isGift && !selectedAddress ? (
                <span>{t("Add Address")}</span>
              ) : (
                <span>{t("Continue to Review")}</span>
              )}
              <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Step 2: Review Order */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {t("Review Your Order")}
              </h1>
              <p className="text-gray-600">
                {t("Please review before placing order")}
              </p>
            </div>

            {/* Delivery Address */}
            {!giftInfo.isGift && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin size={20} className="text-black" />
                    <h3 className="font-semibold text-gray-900">
                      {t("Delivery Address")}
                    </h3>
                  </div>
                  <button
                    onClick={openAddressModal}
                    className="flex items-center gap-1 text-blue-500 hover:text-blue-700 font-medium text-sm"
                  >
                    <Edit size={16} />
                    {t("Edit")}
                  </button>
                </div>
                {selectedAddress ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-semibold text-gray-900 mb-1">
                      {selectedAddress.fullName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedAddress.street}, {selectedAddress.apartment}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedAddress.city}, {selectedAddress.state}{" "}
                      {selectedAddress.zipCode}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedAddress.country}
                    </p>
                    {selectedAddress.phone && (
                      <ContactInfo
                        type="phone"
                        value={selectedAddress.phone}
                        icon={<Phone size={16} />}
                      />
                    )}
                  </div>
                ) : (
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-600">
                      {t("No address selected")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Method */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {selectedPaymentMethod && (
                    <img
                      src={selectedPaymentMethod.icon}
                      alt=""
                      className="w-5 h-5"
                    />
                  )}
                  <h3 className="font-semibold text-gray-900">
                    {t("Payment Method")}
                  </h3>
                </div>
                <button
                  onClick={() => setCurrentStep(1)}
                  className="flex items-center gap-1 text-blue-500 hover:text-blue-700 font-medium text-sm"
                >
                  <Edit size={16} />
                  {t("Edit")}
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                {selectedPaymentMethod && (
                  <>
                    <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                      <img
                        src={selectedPaymentMethod.icon}
                        alt=""
                        className="w-5 h-5 brightness-0 invert"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {i18n.language === "ar"
                          ? selectedPaymentMethod.name.ar
                          : selectedPaymentMethod.name.en}
                      </p>
                      <p className="text-sm text-gray-600">
                        {i18n.language === "ar"
                          ? selectedPaymentMethod?.description?.ar
                          : selectedPaymentMethod?.description?.en}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Gift Information */}
            {giftInfo.isGift && (
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 shadow-sm border-2 border-pink-200">
                <div className="flex items-center gap-2 mb-4">
                  <Gift size={20} className="text-pink-600" />
                  <h3 className="font-semibold text-gray-900">
                    {t("Gift Information")}
                  </h3>
                </div>
                <div className="space-y-3">
                  {/* Recipient Name */}
                  {giftInfo.recipientInfo.recipientName && (
                    <div className="bg-white rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Gift size={16} className="text-pink-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 mb-1">
                            {t("Recipient Name")}
                          </p>
                          <p className="font-semibold text-gray-900">
                            {giftInfo.recipientInfo.recipientName}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recipient Email */}
                  {giftInfo.recipientInfo.recipientEmail && (
                    <div className="bg-white rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Mail size={16} className="text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 mb-1">
                            {t("Recipient Email")}
                          </p>
                          <p className="font-semibold text-gray-900 break-all">
                            {giftInfo.recipientInfo.recipientEmail}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recipient Phone */}
                  {giftInfo.recipientInfo.recipientPhone && (
                    <div className="bg-white rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-[#0d223f] rounded-full flex items-center justify-center flex-shrink-0">
                          <Phone size={16} className="text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 mb-1">
                            {t("Recipient Phone")}
                          </p>
                          <p className="font-semibold text-gray-900">
                            {giftInfo.recipientInfo.recipientPhone}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Gift Message */}
                  {giftInfo.giftMessage && (
                    <div className="bg-white rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <MessageSquare size={16} className="text-green-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 mb-1">
                            {t("Gift Message")}
                          </p>
                          <p className="text-sm text-gray-900 italic leading-relaxed">
                            "{giftInfo.giftMessage}"
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Gift Note */}
                  <div className="bg-pink-100 rounded-lg p-3 flex items-start gap-2">
                    <Gift
                      size={16}
                      className="text-pink-600 mt-0.5 flex-shrink-0"
                    />
                    <p className="text-xs text-pink-800">
                      {t(
                        "This order will be sent as a gift with your personalized message",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">
                {t("Order Summary")}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>
                    {t("Subtotal")} (
                    {cart?.items?.reduce<number>(
                      (sum, item) => sum + item.quantity,
                      0,
                    ) ?? 0}{" "}
                    {t("items")})
                  </span>
                  <span className="font-semibold">
                    <Price amount={Number(cart?.subtotal || 0)} />
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <div className="flex flex-col">
                    <span>{t("Shipping")}</span>
                    {shippingEstimatedDays && (
                      <span className="text-xs text-gray-500">
                        {t("Estimated Delivery")}: {shippingEstimatedDays}
                      </span>
                    )}
                  </div>
                  <span className="font-semibold text-green-600">
                    {destination &&
                    (!shippingRateResolved || shippingLoading) ? (
                      <span className="inline-flex items-center gap-2 text-gray-500">
                        <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        <span>{t("Calculating...")}</span>
                      </span>
                    ) : cart?.shipping === 0 ? (
                      t("Free")
                    ) : (
                      <Price amount={Number(cart?.shipping || 0)} />
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{t("Tax")}</span>
                  <span className="font-semibold">
                    <Price amount={Number(cart?.tax || 0)} />
                  </span>
                </div>
                <div className="border-t-2 border-gray-200 pt-3">
                  <div className="flex justify-between text-gray-900">
                    <span className="text-lg font-bold">{t("Total")}</span>
                    <span className="text-2xl font-bold text-blue-600">
                      <Price amount={Number(cart?.total || 0)} />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Place Order Button */}
            <button
              onClick={handlePlaceOrder}
              disabled={isProcessing}
              className="w-full bg-black hover:bg-black-700 disabled:bg-black-400 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t("Processing...")}</span>
                </>
              ) : (
                <>
                  <Lock size={20} />
                  <span>
                    {selectedPayment?.id === "cash"
                      ? t("Place Order")
                      : t("Proceed to Pay")}
                  </span>
                  <ChevronRight size={20} />
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Lock size={16} />
              <span>
                {t("Your payment information is secure and encrypted")}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Address Selection Modal */}
      {showAddressModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeAddressModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s ease-out" }}
          >
            <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                {t("Select Delivery Address")}
              </h3>
              <button
                onClick={closeAddressModal}
                className="text-white hover:text-gray-200 transition-colors p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6">
              {/* Add New Address Button */}
              <button
                onClick={openAddModal}
                className="w-full mb-4 flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-lg hover:bg-black-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg font-semibold"
              >
                <Plus className="w-5 h-5" />
                <span>{t("Add New Address")}</span>
              </button>

              {/* Address List */}
              {isLoadingAddresses ? (
                <LoadingComponent title={t("Addresses")} />
              ) : addresses.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {t("No addresses yet")}
                  </h3>
                  <p className="text-gray-600">
                    {t("Add your first delivery address to continue")}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      onClick={() => handleSelectAddress(address)}
                      className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                        selectedAddress?.id === address.id
                          ? "border-black shadow-lg"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Default Badge */}
                          {address.isDefault && (
                            <div className="inline-flex items-center gap-1 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold mb-2">
                              <Star className="w-3 h-3 fill-white" />
                              {t("default")}
                            </div>
                          )}

                          <h3 className="text-base font-bold text-gray-800 mb-1">
                            {address.fullName}
                          </h3>
                          <p className="text-sm text-gray-700 mb-1">
                            {address.street}, {address.apartment}
                          </p>
                          <p className="text-sm text-gray-600 mb-1">
                            {address.city}, {address.state}, {address.country}
                          </p>
                          <p className="text-sm text-gray-600 mb-2">
                            {address.zipCode}
                          </p>
                          {address.phone && (
                            <p className="flex items-center gap-2 text-sm text-gray-700">
                              <ContactInfo
                                type="phone"
                                value={address.phone}
                                icon={<Phone size={16} />}
                              />
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(address);
                            }}
                            className="p-2 bg-gray-100 hover:bg-gray-400 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-gray-700" />
                          </button>

                          {selectedAddress?.id === address.id && (
                            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Address Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
          style={{ animation: "fadeIn 0.3s ease-out" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s ease-out" }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onBlur={() => validateForm()}
              style={{ animation: "slideDown 0.3s ease-out" }}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-3 sm:py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
                <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  {editingAddress ? t("Edit Address") : t("Add a New Address")}
                </h3>
                <button
                  onClick={closeAddModalHandler}
                  className="text-white hover:text-gray-300 transition-colors p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Country */}
                  {renderSelectField("country", t("Country"), COUNTRIES)}

                  {/* State */}
                  {/* {renderSelectField("state", t("State"), availableStates)} */}

                  {/* City */}
                  {renderSelectField("city", t("City"), availableCities)}

                  {/* Zip Code */}
                  {/* {renderSelectField("zipCode", t("Zip Code"), availableZipCodes)} */}

                  {/* Qatar-specific fields */}
                  {/* {formData.country === "QA" && (
                    <> */}
                  {/* Zone */}
                  {renderTextField("zone", t("Zone"), true)}

                  {/* Street */}
                  {renderTextField("street", t("Street"))}

                  {/* Building Number */}
                  {renderTextField(
                    "buildingNumber",
                    t("Building Number"),
                    true,
                  )}

                  {/* Unit Number */}
                  {renderTextField("unitNumber", t("Unit Number"), false)}
                  {/* </>
                  )} */}

                  {/* Apartment - optional for Qatar */}
                  {/* {renderTextField("apartment", t("Apartment"), formData.country !== "QA")} */}

                  {/* Full Name */}
                  {renderTextField("fullName", t("Full name"))}

                  {/* Phone */}
                  {renderTextField("phone", t("Phone"), false)}
                </div>

                {/* Qatar Address Info Banner */}
                {formData.country === "QA" && (
                  <div className="mt-4 bg-[#0d223f] border-2 border-[#0d223f] rounded-lg p-3 flex items-start gap-2">
                    <div className="w-5 h-5 bg-[#0d223f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs sm:text-sm text-blue-800 font-semibold mb-1">
                        {t("Qatar Address Format")}
                      </p>
                      <p className="text-xs text-blue-700">
                        {t(
                          "For Qatar addresses, Zone and Building Number are mandatory. Unit Number is optional.",
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Make Default Checkbox */}
                <div className="mt-6 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) =>
                      handleInputChange("isDefault", e.target.checked)
                    }
                    className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-black cursor-pointer"
                  />
                  <label className="text-xs sm:text-sm text-gray-700">
                    {t("Make this my default address")}
                  </label>
                </div>

                {/* Form Status Indicator */}
                {Object.keys(errors).length === 0 &&
                  formData.street &&
                  formData.city &&
                  formData.fullName &&
                  (formData.country !== "QA" ||
                    (formData.zone && formData.buildingNumber))}
              </div>

              {/* Modal Footer */}
              <div className="px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={closeAddModalHandler}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 transition-all duration-300 font-semibold text-sm sm:text-base order-2 sm:order-1"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 transition-all duration-300 font-semibold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base order-1 sm:order-2"
                >
                  {isSaving ? (
                    <>
                      <div
                        className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span>{t("Saving...")}</span>
                    </>
                  ) : editingAddress ? (
                    t("Update Address")
                  ) : (
                    t("Add Address")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MyFatoorah Payment Modal */}
      {showPaymentModal && orderId && cart && (
        <MyFatoorahPayment
          orderId={orderId}
          amount={cart.total}
          paymentMethod={selectedPayment?.type}
          paymentProvider={paymentProvider}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
          onError={handlePaymentError}
        />
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes check {
          0% {
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }

        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }

        .animate-check {
          stroke-dasharray: 100;
          animation: check 0.6s ease-out 0.3s forwards;
        }

        .animate-ping-slow {
          animation: ping-slow 2s ease-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CheckoutPage;
